/**
 * The trust floor, as one piece of global state.
 *
 * The floor used to be a Trusted Finder control: you set it on that page, it
 * applied to that page's results, and every other surface showed whatever eBay
 * returned. The Console redesign makes it a property of the whole site -- the
 * top bar carries the selector, and feed, search, hubs and product all filter
 * to it. That only works if one module owns the value.
 *
 * Precedence is deliberate: ?floor= beats the cookie so a shared link shows the
 * sender's floor, and the cookie beats the account default so a signed-in
 * visitor can change it on one device without changing it everywhere.
 *
 * Thresholds are imported, never restated. trusted-query-parser.js is the
 * source of truth and the server validates against the same table; a second
 * copy here is how the two would drift.
 *
 * Cache note: `uf_floor` is deliberately NOT one of
 * cache_middleware.DEFAULT_IDENTITY_COOKIES. The public paths ("/", "/p/",
 * "/used/", ...) serve one cached body to everyone, so the server renders them
 * at the default floor and this module re-filters after hydration. Making the
 * cookie identity-bearing would be correct and would also drop the edge cache
 * hit rate to nothing, because nearly every returning visitor carries one.
 */

import { TRUSTED_PRESETS } from './trusted-query-parser.js';

/** Ordered as they appear in the selector: loosest to tightest. */
export const FLOORS = ['off', 'standard', 'strict', 'elite'];

export const DEFAULT_FLOOR = 'strict';

/** Segment labels: short enough for a four-across control in a 60px bar. */
export const FLOOR_LABELS = {
  off: 'Off',
  standard: 'Std',
  strict: 'Strict',
  elite: 'Elite',
};

/** Spoken/written form, for the mobile chip, announcements and prose. */
export const FLOOR_NAMES = {
  off: 'Off',
  standard: 'Standard',
  strict: 'Strict',
  elite: 'Elite',
};

const COOKIE_NAME = 'uf_floor';
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365;
const QUERY_PARAM = 'floor';

const listeners = new Set();
let current = null;

/** @returns {boolean} */
export function isFloor(value) {
  return FLOORS.includes(value);
}

/**
 * Thresholds a seller must clear, or null when the floor is off.
 * Returns accepted is a constant of every preset, never a separate switch.
 */
export function thresholds(floor = getFloor()) {
  if (floor === 'off') return null;
  const preset = TRUSTED_PRESETS[floor] || TRUSTED_PRESETS[DEFAULT_FLOOR];
  return {
    feedback: preset.feedback,
    reviews: preset.reviews,
    returnsAccepted: true,
  };
}

/**
 * The floor said in words: `{eyebrow, thresholds, lede}`.
 *
 * A page that filters by the floor has to state the floor, and "Strict" on its
 * own tells a first-time visitor nothing — the thresholds are the content. The
 * server renders these at the default floor (public pages are edge-cached and
 * cannot read the visitor's cookie) and console-shell.js rewrites them from
 * here once the real floor is known. `products/trust_floor.py::floor_prose`
 * is the other half, and test_trust_floor.py asserts the two agree for all
 * four floors.
 */
export function floorProse(floor = getFloor()) {
  const limit = thresholds(floor);
  const name = FLOOR_NAMES[floor] || FLOOR_NAMES.off;
  if (limit === null) {
    return {
      eyebrow: 'All eBay sellers · no seller filter',
      badge: 'Floor off',
      feed: 'every seller',
      thresholds: 'No floor · every seller shown',
      lede:
        'The floor is off — every listing we track is shown, whatever '
        + "the seller's record. Pick a floor in the top bar and it follows "
        + 'you across the whole site.',
    };
  }
  return {
    eyebrow:
      `${name} sellers only · ${limit.feedback}%+ positive `
      + `feedback, ${limit.reviews}+ reviews`,
    badge: `Clears ${name}`,
    feed: `${name} sellers, returns accepted`,
    thresholds: `${limit.feedback}% · ${limit.reviews}+ reviews · returns required`,
    lede:
      `Every listing below comes from an eBay seller at `
      + `${limit.feedback}%+ feedback, ${limit.reviews}+ reviews, `
      + `returns accepted. Change the floor in the top bar; it follows you `
      + `across the whole site.`,
  };
}

function readCookie(name) {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(
    new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()[\]\/+^])/g, '\$1') + '=([^;]*)')
  );
  return match ? decodeURIComponent(match[1]) : null;
}

function writeCookie(name, value) {
  if (typeof document === 'undefined') return;
  const secure = location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${COOKIE_MAX_AGE}; Path=/; SameSite=Lax${secure}`;
}

/**
 * The floor in force right now.
 *
 * Resolved once and cached: every tile asks, and re-parsing the URL and cookie
 * for each one showed up in a 250-row list.
 */
export function getFloor() {
  if (current) return current;

  const fromUrl = new URLSearchParams(location.search).get(QUERY_PARAM);
  if (isFloor(fromUrl)) {
    current = fromUrl;
    return current;
  }

  const fromCookie = readCookie(COOKIE_NAME);
  if (isFloor(fromCookie)) {
    current = fromCookie;
    return current;
  }

  // The account default, server-rendered onto the body so a signed-in visitor
  // does not see the generic default flash before their own setting loads.
  const fromAccount = document.body?.dataset?.floorDefault;
  current = isFloor(fromAccount) ? fromAccount : DEFAULT_FLOOR;
  return current;
}

/**
 * Change the floor. No-op when it already holds, so a selector that re-renders
 * itself on change cannot start a loop.
 *
 * @param {string} floor
 * @param {{silent?: boolean}} [options] silent skips subscribers -- for the
 *   initial sync, where every listener is about to render anyway.
 * @returns {string} the floor now in force
 */
export function setFloor(floor, options = {}) {
  if (!isFloor(floor)) return getFloor();
  if (getFloor() === floor) return current;

  current = floor;
  writeCookie(COOKIE_NAME, floor);
  syncUrl(floor);
  if (document.body) document.body.dataset.floor = floor;

  if (!options.silent) {
    for (const listener of listeners) {
      try {
        listener(floor);
      } catch (error) {
        // One broken subscriber must not stop the rest of the page updating.
        console.error('floor-state listener failed', error);
      }
    }
  }
  return current;
}

/**
 * Keep the URL shareable without littering it: the default floor is implied,
 * anything else is spelled out. replaceState, not pushState -- changing the
 * floor is not a navigation and should not need four Backs to undo.
 */
function syncUrl(floor) {
  if (typeof history?.replaceState !== 'function') return;
  const url = new URL(location.href);
  if (floor === DEFAULT_FLOOR) url.searchParams.delete(QUERY_PARAM);
  else url.searchParams.set(QUERY_PARAM, floor);
  history.replaceState(history.state, '', url);
}

/**
 * Subscribe to changes.
 * @param {(floor: string) => void} callback
 * @returns {() => void} unsubscribe
 */
export function onFloorChange(callback) {
  listeners.add(callback);
  return () => listeners.delete(callback);
}

/**
 * Seller fields as they arrive, whatever spelled them.
 *
 * Four sources use four spellings for the same three numbers: the raw Browse
 * payload (`feedbackPercentage` / `feedbackScore`), the API rows after
 * api-service camelCases them (`sellerFeedbackPct` / `sellerSalesCount`), a
 * Django template context (snake_case), and the normalised shape this function
 * itself returns -- which matters because callers pass the output of one of
 * these back into `clears()` or `tier()`.
 *
 * Missing `sellerSalesCount` from this list is not hypothetical: it is exactly
 * what every row from /api/products/ carries, and while it was absent `clears()`
 * read NaN reviews and rejected the entire feed at any floor.
 */
export function sellerNumbers(seller = {}) {
  const feedback = Number(
    seller.feedback
    ?? seller.feedbackPercentage ?? seller.sellerFeedbackPct ?? seller.feedback_percentage
    ?? NaN
  );
  const reviews = Number(
    seller.reviews
    ?? seller.feedbackScore ?? seller.sellerSalesCount ?? seller.seller_sales_count
    ?? seller.sellerReviews ?? seller.feedback_score
    ?? NaN
  );
  const returns = seller.returns ?? seller.returnsAccepted ?? seller.returns_accepted ?? null;
  return { feedback, reviews, returns };
}

/** Does this seller clear the given floor? */
export function clears(seller, floor = getFloor()) {
  const limit = thresholds(floor);
  if (!limit) return true;
  const { feedback, reviews, returns } = sellerNumbers(seller);
  if (!Number.isFinite(feedback) || !Number.isFinite(reviews)) return false;
  if (feedback < limit.feedback || reviews < limit.reviews) return false;
  return returns !== false;
}

/**
 * Tier for the meter: 0 clears nothing, 3 clears Elite. Same ladder as the
 * Trusted Finder meter, so a seller cannot read as Elite on one page and
 * Standard on another.
 */
export function tier(seller) {
  if (clears(seller, 'elite')) return 3;
  if (clears(seller, 'strict')) return 2;
  if (clears(seller, 'standard')) return 1;
  return 0;
}

/** For fetches: the floor as the API expects it, or nothing when off. */
export function floorParams(floor = getFloor()) {
  const limit = thresholds(floor);
  if (!limit) return {};
  return {
    trustedLevel: floor,
    minSellerFeedbackPct: limit.feedback,
    minSellerReviews: limit.reviews,
    returnsAccepted: true,
  };
}

/** Test seam: forget the resolved value so the next getFloor() re-reads. */
export function _reset() {
  current = null;
  listeners.clear();
}
